import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy, Renderer2, RendererFactory2 } from '@angular/core';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { EnvironmentService } from '@core/feature';
import { UserAgentService } from '@shared/services';
import { PageMeta, PageRobotsMeta, WindowRef } from '@spartacus/core';
import { PageMetaLinkService } from '@spartacus/storefront';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class SeoMetaService implements OnDestroy {
  private renderer: Renderer2;
  private routerSubscription: Subscription;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private environmentService: EnvironmentService,
    private userAgentService: UserAgentService,
    private rendererFactory: RendererFactory2,
    private router: Router,
    private winRef: WindowRef,
    private ngTitle: Title,
    private ngMeta: Meta,
    private pageMetaLinkService?: PageMetaLinkService,
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  init(): void {
    if (this.userAgentService.isSsrBotOrBrowser) {
      this.createAlternateLink();
    }
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  setMeta(meta: PageMeta): void {
    this.title = meta.title ?? '';
    this.ogTitle = meta.ogTitle ?? '';
    this.description = meta.description ?? '';
    this.ogDescription = meta.ogDescription ?? '';
    this.image = meta.image ?? '';
    this.robots = meta.robots ?? [];
    this.canonicalUrl = meta.canonicalUrl ?? '';
    this.type = meta.type ?? '';
  }

  private set title(title: string) {
    this.ngTitle.setTitle(title || '');
  }

  private set ogTitle(value: string) {
    this.addSsrPropertyTag('og:title', value);
  }

  private set description(value: string) {
    if (value) {
      this.addTag({ name: 'description', content: value });
    } else {
      this.ngMeta.removeTag('name="description"');
    }
  }

  private set ogDescription(value: string) {
    this.addSsrPropertyTag('og:description', value);
  }

  private set image(imageUrl: string) {
    if (imageUrl) {
      this.addTag({ name: 'og:image', content: imageUrl });
    } else {
      this.ngMeta.removeTag('name="og:image"');
    }
    this.addSsrPropertyTag('og:image', imageUrl);
  }

  private set robots(value: PageRobotsMeta[] | undefined) {
    if (value && value.length > 0) {
      this.addTag({ name: 'robots', content: value.join(', ') });
    } else {
      this.ngMeta.removeTag('name="robots"');
    }
  }

  private set canonicalUrl(url: string) {
    if (url) {
      this.pageMetaLinkService?.setCanonicalLink(url);
    }
    this.addSsrPropertyTag('og:url', url);
  }

  private set type(type: string) {
    this.addSsrPropertyTag('og:type', type);
  }

  private addTag(meta: MetaDefinition): void {
    if (meta.content) {
      this.ngMeta.updateTag(meta);
    }
  }

  private createAlternateLink(): void {
    let alternateLink: HTMLLinkElement = this.document.head.querySelector('link[rel="alternate"]');
    if (!alternateLink) {
      alternateLink = this.renderer.createElement('link');
      alternateLink.setAttribute('rel', 'alternate');
      alternateLink.setAttribute('media', 'only screen and (max-width: 640px)');
      alternateLink.setAttribute('href', this.document.URL);
      this.renderer.appendChild(this.document.head, alternateLink);
    }

    if (!this.routerSubscription && this.winRef.isBrowser()) {
      this.routerSubscription = this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(() => {
          alternateLink.setAttribute('href', this.document.URL);
        });
    }
  }

  private addSsrPropertyTag(property: string, content: string): void {
    if (this.environmentService.isPlatformServer) {
      this.addTag({ property, content });
    } else {
      this.ngMeta.removeTag(`property="${property}"`);
    }
  }
}
